import { Box, Button, Checkbox, Flex } from '@chakra-ui/react';
import { FC, ReactNode } from 'react';

import { getDays, VisitFrequencyView } from '@app/components/common/visit-frequency';

import { MasterOutletFormInstance } from '../../types';
import { ChangeFieldKey, formatFieldValue, getFieldLabel, resolveContactPersonValue, SelectedFields } from '../../utils/apply-helpers';
import { SectionCard } from './section-card';

const ValueLabel = ({ children, variant }: { children: string; variant: 'current' | 'new' }) => (
  <Box
    as="span"
    fontSize="11px"
    fontWeight={600}
    textTransform="uppercase"
    letterSpacing=".03em"
    color={variant === 'new' ? '#3f9142' : '#98a2b0'}
  >
    {children}
  </Box>
);

const CurrentValueBox = ({ children }: { children: ReactNode }) => (
  <Box
    display="flex"
    alignItems="center"
    gap={2}
    bg="#f2f4f7"
    border="1px solid #d7dce3"
    borderRadius="8px"
    px={3}
    py={1.5}
  >
    {children}
  </Box>
);

const NewValueBox = ({ children }: { children: ReactNode }) => (
  <Box
    bg="#d8f2d8"
    border="1px solid #79c079"
    borderRadius="8px"
    px={3.5}
    py={1.5}
    fontSize="14px"
    color="#1e4620"
    fontWeight={500}
  >
    {children}
  </Box>
);

type ChangedFieldRowProps = {
  label: string;
  isSelected: boolean;
  showCurrent: boolean;
  currentValue: ReactNode;
  newValue: ReactNode;
  onToggle: () => void;
  isLast?: boolean;
  readOnly?: boolean;
};

const ChangedFieldRow: FC<ChangedFieldRowProps> = ({
  label,
  isSelected,
  showCurrent,
  currentValue,
  newValue,
  onToggle,
  isLast,
  readOnly = false,
}) => (
  <Flex
    as={readOnly ? 'div' : 'label'}
    alignItems="flex-start"
    gap={3.5}
    px={6}
    py={4.5}
    borderBottom={isLast ? undefined : '1px solid #eef1f5'}
    cursor={readOnly ? undefined : 'pointer'}
  >
    {!readOnly && (
      <Checkbox
        isChecked={isSelected}
        onChange={onToggle}
        colorScheme="blue"
        size="md"
        mt="2px"
        flex="none"
      />
    )}
    <Box flex={1} minW={0}>
      <Box fontSize="14px" fontWeight={600} color="#1f2937" mb={2.5}>
        {label}
      </Box>
      <Flex alignItems="center" gap={3.5} flexWrap="wrap">
        {showCurrent && (
          <>
            <Flex alignItems="center" gap={2}>
              <ValueLabel variant="current">Текущее</ValueLabel>
              <CurrentValueBox>{currentValue}</CurrentValueBox>
            </Flex>
            <Box color="#b3bcc7" fontSize="18px">→</Box>
          </>
        )}
        <Flex alignItems="center" gap={2}>
          <ValueLabel variant="new">Новое</ValueLabel>
          <NewValueBox>{newValue}</NewValueBox>
        </Flex>
      </Flex>
    </Box>
  </Flex>
);

const CONTACT_PART_LABELS = ['ФИО', 'Телефон', 'Email'] as const;

const normalizeContactPart = (part?: string) => {
  if (!part || part === '<nil>') {
    return '—';
  }
  return part;
};

const splitResolvedContactPerson = (
  newValue: unknown,
  originValue: unknown
): { currentParts: [string, string, string]; newParts: [string, string, string] } => {
  const resolved = resolveContactPersonValue(newValue, originValue);
  const originParts = typeof originValue === 'string' ? originValue.split('|') : [];
  const newParts = resolved.split('|');

  return {
    currentParts: [
      normalizeContactPart(originParts[0]),
      normalizeContactPart(originParts[1]),
      normalizeContactPart(originParts[2]),
    ],
    newParts: [
      normalizeContactPart(newParts[0]),
      normalizeContactPart(newParts[1]),
      normalizeContactPart(newParts[2]),
    ],
  };
};

type ChangedContactPersonRowProps = {
  label: string;
  isSelected: boolean;
  showCurrent: boolean;
  currentValue: unknown;
  newValue: unknown;
  onToggle: () => void;
  isLast?: boolean;
  readOnly?: boolean;
};

const ChangedContactPersonRow: FC<ChangedContactPersonRowProps> = ({
  label,
  isSelected,
  showCurrent,
  currentValue,
  newValue,
  onToggle,
  isLast,
  readOnly = false,
}) => {
  const { currentParts, newParts } = splitResolvedContactPerson(newValue, currentValue);

  return (
    <Flex
      as={readOnly ? 'div' : 'label'}
      alignItems="flex-start"
      gap={3.5}
      px={6}
      py={4.5}
      borderBottom={isLast ? undefined : '1px solid #eef1f5'}
      cursor={readOnly ? undefined : 'pointer'}
    >
      {!readOnly && (
        <Checkbox
          isChecked={isSelected}
          onChange={onToggle}
          colorScheme="blue"
          size="md"
          mt="2px"
          flex="none"
        />
      )}
      <Box flex={1} minW={0}>
        <Box fontSize="14px" fontWeight={600} color="#1f2937" mb={2.5}>
          {label}
        </Box>
        <Flex flexDirection="column" gap={2.5}>
          {CONTACT_PART_LABELS.map((partLabel, index) => (
            <Box key={partLabel}>
              <Box fontSize="12px" fontWeight={500} color="#6b7280" mb={1.5}>
                {partLabel}
              </Box>
              <Flex alignItems="center" gap={3.5} flexWrap="wrap">
                {showCurrent && (
                  <>
                    <Flex alignItems="center" gap={2}>
                      <ValueLabel variant="current">Текущее</ValueLabel>
                      <CurrentValueBox>{currentParts[index]}</CurrentValueBox>
                    </Flex>
                    <Box color="#b3bcc7" fontSize="18px">→</Box>
                  </>
                )}
                <Flex alignItems="center" gap={2}>
                  <ValueLabel variant="new">Новое</ValueLabel>
                  <NewValueBox>{newParts[index]}</NewValueBox>
                </Flex>
              </Flex>
            </Box>
          ))}
        </Flex>
      </Box>
    </Flex>
  );
};

const isContactPersonField = (key: ChangeFieldKey) =>
  key === 'contact_person_1' || key === 'contact_person_2';

const ADDRESS_PARTS = [
  { key: 'full_address', label: 'Полный адрес' },
  { key: 'region', label: 'Регион' },
  { key: 'city', label: 'Город' },
  { key: 'latlng', label: 'Координаты' },
] as const;

type AddressPartKey = (typeof ADDRESS_PARTS)[number]['key'];

const normalizeDisplayValue = (value: unknown): string => {
  if (value === null || value === undefined || value === '' || value === '<nil>') {
    return '—';
  }

  if (Array.isArray(value) && value.length === 2) {
    const formatCoord = (coord: unknown) => {
      const num = Number(coord);
      return Number.isFinite(num) ? num.toFixed(5) : String(coord ?? '');
    };
    return `${formatCoord(value[0])}, ${formatCoord(value[1])}`;
  }

  if (typeof value === 'object' && value !== null) {
    const entity = value as EntityBase;
    if (typeof entity.name === 'string' && entity.name && entity.name !== '<nil>') {
      return entity.name;
    }
    return '—';
  }

  return String(value);
};

const getAddressPartValue = (address: unknown, key: AddressPartKey): string => {
  if (!address || typeof address !== 'object') {
    return '—';
  }

  const record = address as Record<string, unknown>;

  if (key === 'full_address') {
    return normalizeDisplayValue(record.full_address || record.address);
  }

  return normalizeDisplayValue(record[key]);
};

type ChangedAddressRowProps = {
  label: string;
  isSelected: boolean;
  showCurrent: boolean;
  currentValue: unknown;
  newValue: unknown;
  onToggle: () => void;
  isLast?: boolean;
  readOnly?: boolean;
};

const ChangedAddressRow: FC<ChangedAddressRowProps> = ({
  label,
  isSelected,
  showCurrent,
  currentValue,
  newValue,
  onToggle,
  isLast,
  readOnly = false,
}) => (
  <Flex
    as={readOnly ? 'div' : 'label'}
    alignItems="flex-start"
    gap={3.5}
    px={6}
    py={4.5}
    borderBottom={isLast ? undefined : '1px solid #eef1f5'}
    cursor={readOnly ? undefined : 'pointer'}
  >
    {!readOnly && (
      <Checkbox
        isChecked={isSelected}
        onChange={onToggle}
        colorScheme="blue"
        size="md"
        mt="2px"
        flex="none"
      />
    )}
    <Box flex={1} minW={0}>
      <Box fontSize="14px" fontWeight={600} color="#1f2937" mb={2.5}>
        {label}
      </Box>
      <Flex flexDirection="column" gap={2.5}>
        {ADDRESS_PARTS.map((part) => (
          <Box key={part.key}>
            <Box fontSize="12px" fontWeight={500} color="#6b7280" mb={1.5}>
              {part.label}
            </Box>
            <Flex alignItems="center" gap={3.5} flexWrap="wrap">
              {showCurrent && (
                <>
                  <Flex alignItems="center" gap={2}>
                    <ValueLabel variant="current">Текущее</ValueLabel>
                    <CurrentValueBox>
                      {getAddressPartValue(currentValue, part.key)}
                    </CurrentValueBox>
                  </Flex>
                  <Box color="#b3bcc7" fontSize="18px">→</Box>
                </>
              )}
              <Flex alignItems="center" gap={2}>
                <ValueLabel variant="new">Новое</ValueLabel>
                <NewValueBox>
                  {getAddressPartValue(newValue, part.key)}
                </NewValueBox>
              </Flex>
            </Flex>
          </Box>
        ))}
      </Flex>
    </Box>
  </Flex>
);

type ChangedFieldsSectionProps = {
  origin?: Partial<MasterOutletFormInstance>;
  instance: Partial<MasterOutletFormInstance>;
  changedFields: ChangeFieldKey[];
  selectedFields: SelectedFields;
  showCurrentValues: boolean;
  selectedCount: number;
  totalCount: number;
  allSelected: boolean;
  isSubmitting?: boolean;
  onToggleField: (key: ChangeFieldKey) => void;
  onToggleAll: () => void;
  onApply: () => void;
  onDecline?: () => void;
  readOnly?: boolean;
};

export const ChangedFieldsSection: FC<ChangedFieldsSectionProps> = ({
  origin,
  instance,
  changedFields,
  selectedFields,
  showCurrentValues,
  selectedCount,
  totalCount,
  allSelected,
  isSubmitting,
  onToggleField,
  onToggleAll,
  onApply,
  onDecline,
  readOnly = false,
}) => {
  const renderFieldValue = (key: ChangeFieldKey, value: unknown) => {
    if (key === 'visits_day' && typeof value === 'string') {
      const [, days] = getDays(value);
      return <VisitFrequencyView days={days} />;
    }

    return formatFieldValue(key, value);
  };

  return (
    <SectionCard
      title="Изменённые поля"
      subtitle={readOnly
        ? 'Изменения, предложенные в заявке.'
        : 'Отметьте изменения, которые нужно применить к торговой точке.'}
      mb={0}
    >
      {changedFields.length === 0 ? (
        <Box px={6} py={4} fontSize="14px" color="#8a94a3">
          Нет изменённых полей
        </Box>
      ) : (
        <>
          {changedFields.map((key, index) => {
            const commonProps = {
              label: getFieldLabel(key),
              isSelected: !!selectedFields[key],
              showCurrent: showCurrentValues,
              onToggle: () => onToggleField(key),
              isLast: index === changedFields.length - 1,
              readOnly,
            };

            if (isContactPersonField(key)) {
              return (
                <ChangedContactPersonRow
                  key={key}
                  {...commonProps}
                  currentValue={origin?.[key]}
                  newValue={instance[key]}
                />
              );
            }

            if (key === 'address') {
              return (
                <ChangedAddressRow
                  key={key}
                  {...commonProps}
                  currentValue={origin?.address}
                  newValue={instance.address}
                />
              );
            }

            return (
              <ChangedFieldRow
                key={key}
                {...commonProps}
                currentValue={renderFieldValue(key, origin?.[key])}
                newValue={renderFieldValue(key, instance[key])}
              />
            );
          })}
        </>
      )}

      {!readOnly && (
      <Flex
        alignItems="center"
        justifyContent="space-between"
        gap={4}
        px={6}
        py={4.5}
        bg="#f7f9fb"
        borderTop="1px solid #eef1f5"
        flexWrap="wrap"
      >
        <Flex
          as="label"
          alignItems="center"
          gap={2}
          fontSize="13.5px"
          color="#3a4150"
          cursor="pointer"
          userSelect="none"
        >
          <Checkbox
            isChecked={allSelected}
            onChange={onToggleAll}
            colorScheme="blue"
            size="md"
            isDisabled={totalCount === 0}
          />
          <Box as="span">
            Выбрать все ·{' '}
            <Box as="span" color="#6b7280">
              выбрано{' '}
              <Box as="span" fontWeight={600} color="#1f2937">
                {selectedCount}
              </Box>
              {' '}из {totalCount}
            </Box>
          </Box>
        </Flex>

        <Flex alignItems="center" gap={2.5}>
          {onDecline && (
            <Button
              variant="outline"
              borderColor="#d7dce3"
              color="#c0392b"
              fontWeight={500}
              borderRadius="9px"
              px={4.5}
              py={2}
              h="auto"
              _hover={{ bg: '#fdecea', borderColor: '#e6a8a0' }}
              onClick={onDecline}
              isDisabled={isSubmitting}
            >
              Отклонить
            </Button>
          )}
          <Button
            bg="#1a6ff2"
            color="white"
            fontWeight={500}
            borderRadius="9px"
            px={5}
            py={2}
            h="auto"
            _hover={{ bg: '#1257c9' }}
            onClick={onApply}
            isLoading={isSubmitting}
            isDisabled={selectedCount === 0}
          >
            Применить выбранные
            <Box
              as="span"
              ml={2}
              bg="rgba(255,255,255,.25)"
              borderRadius="12px"
              px={2}
              fontSize="12.5px"
              fontWeight={600}
              minW="20px"
              textAlign="center"
            >
              {selectedCount}
            </Box>
          </Button>
        </Flex>
      </Flex>
      )}
    </SectionCard>
  );
};
